import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from "@angular/core";
import isEqual from "lodash-es/isEqual";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

import {
    AnalyticsService,
    EcomOnlyAnalyticsEvent,
} from "@hermes/utils/analytics";
import { FilterHatchService } from "@hermes/pages/grid-page";
import {
    GRID_SORT_BUTTON_ID,
    GridFacade,
    GridFilterGroup,
    GridSortByOption,
    Sort,
} from "@hermes/states/grid-filters-state";
import { FocusService } from "@hermes/utils-generic/services/focus";

import { GridToggleComponent } from "../grid-toggle/grid-toggle.component";

@Component({
    selector: "h-grid-filters-responsive-menu",
    templateUrl: "./grid-filters-responsive-menu.component.html",
    styleUrls: ["./grid-filters-responsive-menu.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFiltersResponsiveMenuComponent implements OnInit, OnDestroy {
    @Input()
    public selectedValue: Sort;

    @Input()
    public mobileMode = false;

    @ViewChildren("gridFilterToggle")
    private gridFilterToggleEls: QueryList<GridToggleComponent>;

    public gridFilterGroups: GridFilterGroup[] = [];
    public filterGroupOpened: string = undefined;

    private subscription: Subscription = new Subscription();
    private focusFirstToggle = false;

    constructor(
        private gridFacade: GridFacade,
        private filterHatchService: FilterHatchService,
        private focusService: FocusService,
        private cd: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.gridFacade.filterGroups$
                .pipe(distinctUntilChanged(isEqual))
                .subscribe((gridFilterGroups: GridFilterGroup[]) => {
                    this.gridFilterGroups = gridFilterGroups;

                    /* Sort filters to display 'switch' first */
                    this.gridFilterGroups.sort((a, _b) =>
                        a.presentation === "switch" ? -1 : 0,
                    );

                    this.cd.markForCheck();
                    this.handleFocus();
                }),
        );

        this.subscription.add(
            this.filterHatchService.cleared$.subscribe(() => {
                this.focusFirstToggle = true;
                this.filterGroupOpened = undefined;
                this.cd.markForCheck();
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onCollapsedChange(filterGroupOpened: string): void {
        this.filterGroupOpened = filterGroupOpened;
        this.cd.markForCheck();
    }

    /**
     * Gives focus to the first category (grid-toggle-component) after a clear event.
     */
    public focusFirstOfToggle(): void {
        if (!this.gridFilterToggleEls) {
            return;
        }
        const gridToggleComponents = this.gridFilterToggleEls.toArray();
        const firstToggle = gridToggleComponents[0];
        if (!firstToggle?.filterButtonEl) {
            return;
        }
        this.focusService.elementToFocus =
            firstToggle.filterButtonEl.nativeElement.id;
        this.focusFirstToggle = false;
        this.handleFocus();
    }

    /**
     * when selected sort change
     */
    public onSortChanged(sort: GridSortByOption): void {
        this.gridFacade.updateGridSort(sort);
        this.focusService.elementToFocus = GRID_SORT_BUTTON_ID;
    }

    public trackByFilterGroup(
        index: number,
        filterGroup: GridFilterGroup,
    ): string {
        return `${index}_${filterGroup?.name}`;
    }

    /**
     * Must give focus to grid-checkbox / grid-colors-sizes or grid-slider depending on user interaction.
     */
    private handleFocus(): void {
        if (this.focusFirstToggle) {
            this.focusFirstOfToggle();
        } else {
            // Do not scroll top if mobile mode.
            this.focusService.focusElementOnLoaderClose(!this.mobileMode, true);
        }
    }
}
